import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase';
import { getUserIntegrationsStatus } from './userIntegrationService';
import {
  PROPOSAL_JOB_STATUSES,
  PROPOSAL_DOC_TYPES,
  DEFAULT_PROPOSAL_PROBABILITY,
  isClosedStatus,
  isActiveStatus
} from './jobtreadConstants';

/**
 * JobTread API Service
 * Handles grant-based authentication and Pave query language requests
 * Uses Firebase Functions (onCall) to avoid CORS issues
 * Authentication is automatically handled by Firebase
 */

/**
 * Is JobTread connected for this user? Reads the non-secret
 * integrations.jobtread.connected flag (internal_users first, then users).
 * The grant key itself lives in server-only storage.
 */
export const isJobTreadConnected = async (userId) => {
  if (!userId) return false;
  try {
    const integrations = await getUserIntegrationsStatus(userId);
    return integrations?.jobtread?.connected === true;
  } catch (error) {
    console.error('Error checking JobTread connection:', error);
    return false;
  }
};

/**
 * Make a Pave query to the JobTread API through the makeJobTreadQuery callable.
 *
 * The browser never holds a stored grant key: the server resolves it. Pass
 * `targetUserId` when an internal user views a client's data (e.g. CRM Portal
 * View) — every org-level function below accepts { targetUserId } and threads it
 * here explicitly. Omit it (or pass the caller's own uid) to use the caller's
 * own grant. A module-global override used to serve that purpose, but
 * overlapping hook runs (tab/client switches, React StrictMode) cleared it
 * mid-flight and poisoned in-progress fetches — keep it explicit per call.
 *
 * `options.grantKey` is only for testing a key the user just typed.
 */
export const makeJobTreadQuery = async (query, targetUserId = null, { grantKey = null } = {}) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    const payload = { query };
    if (grantKey) payload.grantKey = grantKey;
    if (targetUserId && targetUserId !== user.uid) payload.targetUserId = targetUserId;

    const makeJobTreadQueryFunction = httpsCallable(functions, 'makeJobTreadQuery');
    const result = await makeJobTreadQueryFunction(payload);

    if (result.data && result.data.success) {
      return result.data.data;
    } else {
      const errorMessage = result.data?.error || 'Failed to make JobTread query';
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error('Error making JobTread query:', error);
    // Extract a more user-friendly error message from Firebase errors
    if (error.code === 'functions/internal' || error.code === 'internal') {
      const message = error.message || 'An internal error occurred';
      throw new Error(message);
    }
    // For other Firebase errors, extract the message
    if (error.message) {
      throw new Error(error.message);
    }
    throw error;
  }
};

/**
 * Get current user's organizations
 * Schema: currentGrant (grant) -> user -> memberships -> nodes -> organization
 */
export const getJobTreadOrganizations = async ({ targetUserId = null } = {}) => {
  const query = {
    currentGrant: {
      id: {},
      user: {
        id: {},
        memberships: {
          nodes: {
            organization: {
              id: {},
              name: {}
            }
          }
        }
      }
    }
  };

  return await makeJobTreadQuery(query, targetUserId);
};

/**
 * Get organization by ID
 */
export const getJobTreadOrganization = async (organizationId) => {
  const query = {
    organization: {
      $: { id: organizationId },
      id: {},
      name: {},
      accounts: {
        nextPage: {},
        nodes: {
          id: {},
          name: {},
          type: {}
        }
      }
    }
  };

  return await makeJobTreadQuery(query);
};

/**
 * Get accounts (customers/vendors) for an organization
 */
export const getJobTreadAccounts = async (organizationId, options = {}) => {
  const { size = 25, page = null, sortBy = null, where = null, targetUserId = null } = options;

  const query = {
    organization: {
      $: { id: organizationId },
      id: {},
      accounts: {
        $: {
          ...(size && { size }),
          ...(page && { page }),
          ...(sortBy && { sortBy }),
          ...(where && { where })
        },
        nextPage: {},
        previousPage: {},
        nodes: {
          id: {},
          name: {},
          type: {},
          isTaxable: {},
          createdAt: {}
        }
      }
    }
  };

  return await makeJobTreadQuery(query, targetUserId);
};

/**
 * Get customers for an organization
 * Uses Pave where: and: [['type', '=', 'customer']] per JobTread API docs
 */
export const getJobTreadCustomers = async (organizationId, options = {}) => {
  return getJobTreadAccounts(organizationId, {
    ...options,
    where: { and: [['type', '=', 'customer']] },
    size: options.size ?? 100
  });
};

const BASIC_ACCOUNT_NODES = { id: {}, name: {}, type: {}, createdAt: {} };
// Accounts carry org-defined custom fields too (lead source, pipeline stage,
// etc.). Mirrors ENRICHED_JOB_NODES so the same field-mapping resolves them.
const ENRICHED_ACCOUNT_NODES = {
  ...BASIC_ACCOUNT_NODES,
  customFieldValues: {
    $: { size: 25 },
    nodes: {
      id: {},
      value: {},
      customField: { id: {}, name: {} }
    }
  }
};

const buildAccountsQuery = (organizationId, options = {}, nodes = BASIC_ACCOUNT_NODES) => {
  const { size = 100, page = null, sortBy = null, where = null } = options;
  return {
    organization: {
      $: { id: organizationId },
      id: {},
      accounts: {
        $: {
          ...(size && { size }),
          ...(page && { page }),
          ...(sortBy && { sortBy }),
          ...(where && { where })
        },
        nextPage: {},
        previousPage: {},
        nodes
      }
    }
  };
};

/**
 * Customer accounts including custom-field values. Org-unsupported fields are
 * dropped individually (see queryWithNodeFallback on jobs) so custom fields
 * survive schema differences.
 */
export const getJobTreadCustomersEnriched = async (organizationId, options = {}) => {
  const base = { ...options, where: { and: [['type', '=', 'customer']] }, size: options.size ?? 100 };
  return queryWithNodeFallback(
    (nodes) => buildAccountsQuery(organizationId, base, nodes),
    ENRICHED_ACCOUNT_NODES,
    BASIC_ACCOUNT_NODES,
    options.targetUserId ?? null
  );
};

/**
 * Customer accounts as a unified lead list (mirrors getJobTreadLeads shape) so
 * "New Customer" can drive the leads chart and lead-source attribution. Lead
 * source comes from the mapped custom field via each account's values.
 *
 * @param {string} organizationId
 * @param {{ mapping?: Object|null, targetUserId?: string|null }} [options]
 * @returns {Promise<{ leads: Array, diagnostics: Object }>}
 */
export const getJobTreadCustomerLeads = async (organizationId, { mapping = null, targetUserId = null } = {}) => {
  const diagnostics = {
    source: 'jobtread-customers', orgId: organizationId, enriched: false, enrichError: null,
    total: 0, leadsWithLeadSource: 0, warnings: [], error: null
  };
  try {
    // Paged small so account customFieldValues stay under JobTread's response
    // limit (same constraint as jobs).
    const { items: list, enriched, enrichError, droppedFields } = await fetchEnrichedPaged({
      buildQuery: (opts, nodes) => buildAccountsQuery(organizationId, opts, nodes),
      getConnection: (data) => data?.organization?.accounts,
      fullNodes: ENRICHED_ACCOUNT_NODES,
      basicNodes: BASIC_ACCOUNT_NODES,
      maxItems: 100,
      pageSize: 20,
      targetUserId,
      baseOptions: {
        where: { and: [['type', '=', 'customer']] },
        sortBy: [{ field: 'createdAt', order: 'desc' }]
      }
    });
    diagnostics.enriched = enriched;
    diagnostics.enrichError = enrichError;
    diagnostics.droppedFields = droppedFields || [];
    if (droppedFields?.length) {
      diagnostics.warnings.push(`Org schema does not support customer field${droppedFields.length === 1 ? '' : 's'}: ${droppedFields.join(', ')}.`);
    }
    diagnostics.total = list.length;
    const leadSourceFieldId = mapping?.fields?.leadSource?.fieldId || null;

    const fieldsSeen = new Map();
    const leads = list.map((acc) => {
      if (enriched) collectCustomFieldDefs(acc, fieldsSeen);
      const cfv = enriched ? extractCustomFieldValues(acc) : {};
      const leadSource = (leadSourceFieldId ? cfv[leadSourceFieldId] : null) ?? null;
      if (leadSource) diagnostics.leadsWithLeadSource += 1;
      return {
        leadId: acc.id,
        jobId: null,
        createdDate: acc.createdAt ?? null,
        status: '', // accounts have no reliable built-in status — use a custom field
        closedOn: null,
        customerId: acc.id,
        customerName: acc.name ?? null,
        estimatedProjectValue: null,
        leadSource,
        customFields: cfv,
        name: acc.name || '—'
      };
    });

    if (!enriched) {
      diagnostics.warnings.push(`Enriched customer query failed (${enrichError}) — customer custom fields (lead source, status) are unavailable this session.`);
    }
    diagnostics.customFieldsSeen = [...fieldsSeen].map(([id, name]) => ({ id, name }));
    return { leads, diagnostics };
  } catch (e) {
    console.error('getJobTreadCustomerLeads error:', e);
    diagnostics.error = e?.message || String(e);
    return { leads: [], diagnostics };
  }
};

/**
 * Get vendors for an organization
 * Uses Pave where: and: [['type', '=', 'vendor']] per JobTread API docs
 */
export const getJobTreadVendors = async (organizationId, options = {}) => {
  return getJobTreadAccounts(organizationId, {
    ...options,
    where: { and: [['type', '=', 'vendor']] },
    size: options.size ?? 100
  });
};

/**
 * Create a new account (customer or vendor)
 */
export const createJobTreadAccount = async (organizationId, name, type) => {
  const query = {
    createAccount: {
      $: {
        organizationId,
        name,
        type
      },
      createdAccount: {
        id: {},
        name: {},
        createdAt: {},
        type: {},
        organization: {
          id: {},
          name: {}
        }
      }
    }
  };

  return await makeJobTreadQuery(query);
};

const BASIC_JOB_NODES = {
  id: {},
  name: {},
  number: {},
  status: {},
  createdAt: {}
};

// Business fields like contract value and lead source are org-defined CUSTOM
// FIELDS in JobTread — they are NOT top-level job columns. The enriched node
// set pulls customFieldValues (plus the customer account and closedOn) so the
// portal can resolve them through the field mapping.
const ENRICHED_JOB_NODES = {
  ...BASIC_JOB_NODES,
  closedOn: {},
  account: {
    id: {},
    name: {}
  },
  customFieldValues: {
    $: { size: 25 },
    nodes: {
      id: {},
      value: {},
      customField: {
        id: {},
        name: {}
      }
    }
  }
};

const buildJobsQuery = (organizationId, options = {}, nodes = BASIC_JOB_NODES) => {
  const { size = 25, page = null, sortBy = null, where = null } = options;
  return {
    organization: {
      $: { id: organizationId },
      id: {},
      jobs: {
        $: {
          ...(size && { size }),
          ...(page && { page }),
          ...(sortBy && { sortBy }),
          ...(where && { where })
        },
        nextPage: {},
        previousPage: {},
        nodes
      }
    }
  };
};

/**
 * Get jobs for an organization
 */
export const getJobTreadJobs = async (organizationId, options = {}) => {
  return await makeJobTreadQuery(buildJobsQuery(organizationId, options, BASIC_JOB_NODES), options.targetUserId ?? null);
};

/** Matches Pave's invalid-field error, e.g. `The field "account" does not exist at ...` */
const INVALID_FIELD_RE = /The field "([^"]+)" does not exist/i;

/**
 * Run a nodes-parameterized Pave query, self-healing when the org's schema
 * rejects individual enriched fields. A Pave query fails AS A WHOLE on any
 * invalid field, and some orgs lack e.g. "account" on jobs — previously one
 * such field cost ALL enrichment (customFieldValues included, so custom
 * statuses / lead sources vanished). Now the named bad field is dropped and
 * the query retried; only unparseable failures fall back to basicNodes.
 *
 * @returns {Promise<{ data: Object, enriched: boolean, enrichError: string|null, droppedFields: string[] }>}
 */
const queryWithNodeFallback = async (buildQuery, fullNodes, basicNodes, targetUserId = null) => {
  let nodes = { ...fullNodes };
  const dropped = [];
  for (let attempt = 0; attempt < 4; attempt++) {
    try {
      const data = await makeJobTreadQuery(buildQuery(nodes), targetUserId);
      return { data, enriched: true, enrichError: null, droppedFields: dropped, nodesUsed: nodes };
    } catch (e) {
      const bad = INVALID_FIELD_RE.exec(e?.message || '')?.[1];
      // Bail to basic when the failure isn't a droppable top-level field
      // (unparseable error, nested field, or a field basic needs too).
      if (!bad || !(bad in nodes) || bad in basicNodes) {
        console.warn('Enriched JobTread query failed, retrying with basic fields:', e?.message);
        const data = await makeJobTreadQuery(buildQuery(basicNodes), targetUserId);
        return { data, enriched: false, enrichError: e?.message || String(e), droppedFields: dropped, nodesUsed: basicNodes };
      }
      console.warn(`Enriched JobTread query: org schema rejects "${bad}" — dropping it and retrying`);
      nodes = { ...nodes };
      delete nodes[bad];
      dropped.push(bad);
    }
  }
  const data = await makeJobTreadQuery(buildQuery(basicNodes), targetUserId);
  return { data, enriched: false, enrichError: 'Too many unsupported enriched fields', droppedFields: dropped, nodesUsed: basicNodes };
};

const RESPONSE_TOO_LARGE_RE = /request entity too large/i;

/**
 * Fetch an enriched connection (jobs / accounts) in SMALL PAGES. JobTread
 * rejects large responses with "Request Entity Too Large" — 100 items with
 * customFieldValues each is over that limit for busy orgs, which previously
 * forced the basic-fields fallback and lost all custom fields. Field-drop
 * discovery runs on the first page only; surviving nodes are reused for the
 * rest. If even the first enriched page is too large, the page size halves
 * once before accepting the basic fallback.
 *
 * @param {{
 *   buildQuery: (opts: Object, nodes: Object) => Object,
 *   getConnection: (data: Object) => { nodes?: any, nextPage?: any } | undefined,
 *   fullNodes: Object, basicNodes: Object,
 *   maxItems?: number, pageSize?: number, baseOptions?: Object, targetUserId?: string|null
 * }} params
 * @returns {Promise<{ items: any[], enriched: boolean, enrichError: string|null, droppedFields: string[], hasMore: boolean }>}
 */
const fetchEnrichedPaged = async (params) => {
  const {
    buildQuery, getConnection, fullNodes, basicNodes,
    maxItems = 100, pageSize = 20, baseOptions = {}, targetUserId = null
  } = params;
  let page = null;
  let nodesUsed = null;
  let enriched = true;
  let enrichError = null;
  let droppedFields = [];
  const items = [];
  let hasMore = false;
  const maxPages = Math.max(1, Math.ceil(maxItems / pageSize));

  for (let i = 0; i < maxPages; i++) {
    const opts = { ...baseOptions, size: pageSize, ...(page ? { page } : {}) };
    let data;
    if (!nodesUsed) {
      const res = await queryWithNodeFallback((nodes) => buildQuery(opts, nodes), fullNodes, basicNodes, targetUserId);
      if (!res.enriched && RESPONSE_TOO_LARGE_RE.test(res.enrichError || '') && pageSize > 10) {
        // Even one enriched page was too large — halve the page and restart.
        return fetchEnrichedPaged({ ...params, pageSize: Math.max(10, Math.floor(pageSize / 2)) });
      }
      ({ data, enriched, enrichError, droppedFields } = res);
      nodesUsed = res.nodesUsed;
    } else {
      try {
        data = await makeJobTreadQuery(buildQuery(opts, nodesUsed), targetUserId);
      } catch (e) {
        // A later page failed — keep what we have rather than losing the run.
        console.warn('JobTread paged fetch stopped early:', e?.message);
        enrichError = enrichError || e?.message || String(e);
        hasMore = true;
        break;
      }
    }
    const conn = getConnection(data);
    const nodes = conn?.nodes ?? [];
    const list = (Array.isArray(nodes) ? nodes : [nodes]).filter(Boolean);
    items.push(...list);
    page = conn?.nextPage || null;
    hasMore = !!page;
    if (!page || list.length === 0 || items.length >= maxItems) break;
  }

  return { items, enriched, enrichError, droppedFields, hasMore };
};

/**
 * Get jobs including custom field values, closedOn and customer account.
 * Org-unsupported fields are dropped individually (see queryWithNodeFallback)
 * so customFieldValues survives orgs whose jobs lack "account" or "closedOn".
 *
 * @returns {Promise<{ data: Object, enriched: boolean, enrichError: string|null, droppedFields: string[] }>}
 */
export const getJobTreadJobsEnriched = async (organizationId, options = {}) =>
  queryWithNodeFallback(
    (nodes) => buildJobsQuery(organizationId, options, nodes),
    ENRICHED_JOB_NODES,
    BASIC_JOB_NODES,
    options.targetUserId ?? null
  );

/** Build a { [customFieldId]: value } map from an enriched job node. */
export const extractCustomFieldValues = (job) => {
  const nodes = job?.customFieldValues?.nodes ?? [];
  const list = Array.isArray(nodes) ? nodes : [nodes];
  const map = {};
  list.forEach((n) => {
    const fieldId = n?.customField?.id;
    if (fieldId != null) map[fieldId] = n?.value ?? null;
  });
  return map;
};

/**
 * Accumulate custom-field DEFINITIONS ({id, name}) observed on enriched nodes
 * into `seen` (a Map). Ground truth for the lead-settings pickers: fields that
 * demonstrably exist on this entity's records, independent of the separate
 * org-definitions query (which can be cached stale or filtered out).
 */
const collectCustomFieldDefs = (node, seen) => {
  const nodes = node?.customFieldValues?.nodes ?? [];
  const list = Array.isArray(nodes) ? nodes : [nodes];
  list.forEach((n) => {
    const f = n?.customField;
    if (f?.id != null && !seen.has(f.id)) seen.set(f.id, f.name || `Field ${f.id}`);
  });
};

/** Parse a currency-ish custom field value ("$450,000", "450000") to a number or null. */
const parseCurrencyValue = (v) => {
  if (v == null || v === '') return null;
  const n = parseFloat(String(v).replace(/[,$\s]/g, ''));
  return Number.isFinite(n) ? n : null;
};

/**
 * Get documents (invoices, orders, etc.) for an organization
 */
export const getJobTreadDocuments = async (organizationId, options = {}) => {
  const { size = 25, page = null, sortBy = null, where = null, targetUserId = null } = options;

  const query = {
    organization: {
      $: { id: organizationId },
      id: {},
      documents: {
        $: {
          ...(size && { size }),
          ...(page && { page }),
          ...(sortBy && { sortBy }),
          ...(where && { where })
        },
        nextPage: {},
        previousPage: {},
        nodes: {
          id: {},
          name: {},
          type: {},
          status: {},
          cost: {},
          price: {},
          tax: {},
          number: {},
          createdAt: {},
          job: {
            id: {},
            name: {},
            number: {}
          }
        }
      }
    }
  };

  return await makeJobTreadQuery(query, targetUserId);
};

/**
 * Get customer orders (signed/approved contracts) for sales analytics.
 * Revenue = approved order prices; projects sold = distinct jobs with an
 * approved order. Dated by the document's createdAt.
 *
 * @param {string} organizationId
 * @param {{ targetUserId?: string|null }} [options]
 * @returns {Promise<{ orders: Array<{id,name,status,price,createdAt,jobId,jobName}>, diagnostics: Object }>}
 */
export const getJobTreadCustomerOrders = async (organizationId, { targetUserId = null } = {}) => {
  const diagnostics = { total: 0, approved: 0, withPrice: 0, withCost: 0, pages: 0, statusCounts: {}, warnings: [], error: null };
  try {
    // Paginate so orgs with many orders aren't truncated at 100 (cap 500).
    const orders = [];
    let page = null;
    for (let i = 0; i < 5; i += 1) {
      const data = await getJobTreadDocuments(organizationId, {
        size: 100,
        targetUserId,
        ...(page && { page }),
        where: { and: [['type', '=', 'customerOrder']] },
        sortBy: [{ field: 'createdAt', order: 'desc' }]
      });
      const documentsNode = data?.organization?.documents;
      const nodes = documentsNode?.nodes ?? [];
      const list = (Array.isArray(nodes) ? nodes : [nodes]).filter(Boolean);
      list.forEach((d) => {
        orders.push({
          id: d.id,
          name: d.name || d.number || '—',
          status: String(d.status || '').toLowerCase(),
          price: parseCurrencyValue(d.price) ?? 0,
          // Estimated cost on the order — powers gross profit / margin analytics.
          cost: parseCurrencyValue(d.cost) ?? null,
          createdAt: d.createdAt ?? null,
          jobId: d.job?.id ?? null,
          jobName: d.job?.name ?? null
        });
      });
      diagnostics.pages += 1;
      page = documentsNode?.nextPage || null;
      if (!page || list.length === 0) break;
    }
    if (page) {
      diagnostics.warnings.push('More than 500 customer orders — the oldest are not included.');
    }
    orders.forEach((o) => {
      const key = o.status || '(blank)';
      diagnostics.statusCounts[key] = (diagnostics.statusCounts[key] || 0) + 1;
    });
    diagnostics.total = orders.length;
    diagnostics.approved = orders.filter((o) => o.status.includes('approved') || o.status.includes('signed')).length;
    diagnostics.withPrice = orders.filter((o) => o.price > 0).length;
    diagnostics.withCost = orders.filter((o) => o.cost != null && o.cost > 0).length;
    if (diagnostics.total === 0) {
      diagnostics.warnings.push('No customer orders found in JobTread.');
    } else if (diagnostics.approved === 0) {
      diagnostics.warnings.push('Customer orders exist but none are approved/signed.');
    }
    if (diagnostics.total > 0 && diagnostics.withCost === 0) {
      diagnostics.warnings.push('No customer orders carry a cost — gross profit/margin analytics are unavailable.');
    }
    return { orders, diagnostics };
  } catch (e) {
    console.error('getJobTreadCustomerOrders error:', e);
    diagnostics.error = e?.message || String(e);
    return { orders: [], diagnostics };
  }
};

/**
 * Get open invoices
 */
export const getJobTreadOpenInvoices = async (organizationId) => {
  const query = {
    organization: {
      $: { id: organizationId },
      id: {},
      documents: {
        $: {
          where: {
            and: [
              ['type', '=', 'customerInvoice'],
              ['status', '=', 'pending'],
              ['price', '>', 0]
            ]
          },
          sortBy: [
            { field: 'price', order: 'desc' }
          ],
          size: 5
        },
        nextPage: {},
        nodes: {
          id: {},
          cost: {},
          price: {},
          tax: {},
          name: {},
          number: {},
          job: {
            id: {},
            name: {},
            number: {}
          }
        }
      }
    }
  };

  return await makeJobTreadQuery(query);
};

/**
 * Sum all outstanding (pending) customer invoices — receivables for the
 * finance dashboard. Paginates so large orgs aren't truncated (cap 500).
 *
 * @param {string} organizationId
 * @param {{ targetUserId?: string|null }} [options]
 * @returns {Promise<{ total: number, count: number, diagnostics: Object }>}
 */
export const getJobTreadReceivables = async (organizationId, { targetUserId = null } = {}) => {
  const diagnostics = { pages: 0, warnings: [], error: null };
  try {
    let total = 0;
    let count = 0;
    let page = null;
    for (let i = 0; i < 5; i += 1) {
      const data = await getJobTreadDocuments(organizationId, {
        size: 100,
        targetUserId,
        ...(page && { page }),
        where: {
          and: [
            ['type', '=', 'customerInvoice'],
            ['status', '=', 'pending'],
            ['price', '>', 0]
          ]
        },
        sortBy: [{ field: 'price', order: 'desc' }]
      });
      const documentsNode = data?.organization?.documents;
      const nodes = documentsNode?.nodes ?? [];
      const list = (Array.isArray(nodes) ? nodes : [nodes]).filter(Boolean);
      for (const d of list) {
        total += parseCurrencyValue(d.price) ?? 0;
        count += 1;
      }
      diagnostics.pages += 1;
      page = documentsNode?.nextPage || null;
      if (!page || list.length === 0) break;
    }
    if (page) {
      diagnostics.warnings.push('More than 500 open invoices — the smallest are not included.');
    }
    if (count === 0) {
      diagnostics.warnings.push('No pending customer invoices found in JobTread.');
    }
    return { total: Math.round(total), count, diagnostics };
  } catch (e) {
    console.error('getJobTreadReceivables error:', e);
    diagnostics.error = e?.message || String(e);
    return { total: 0, count: 0, diagnostics };
  }
};

/**
 * Sum all outstanding (pending) vendor bills — accounts payable for the
 * finance dashboard. A vendor bill (money the builder owes) is exposed by
 * JobTread under different type names across orgs/schema versions, so we probe
 * every candidate and AGGREGATE across the ones that return data rather than
 * stopping at the first — a valid-but-empty type must not short-circuit a
 * different type that actually holds the bills. Document types are mutually
 * exclusive, so summing across candidates never double-counts. Purchase orders
 * are intentionally excluded: a commitment isn't an amount owed yet. Paginates
 * like receivables (cap 500 per type). The dev panel surfaces `byType` so the
 * exact type in use can be confirmed against a live org.
 *
 * @param {string} organizationId
 * @param {{ targetUserId?: string|null }} [options]
 * @returns {Promise<{ total: number, count: number, diagnostics: Object }>}
 */
export const getJobTreadPayables = async (organizationId, { targetUserId = null } = {}) => {
  const TYPE_CANDIDATES = ['bill', 'vendorBill'];
  const diagnostics = { pages: 0, typesQueried: TYPE_CANDIDATES, byType: {}, typeUsed: null, warnings: [], error: null };
  let total = 0;
  let count = 0;
  let anySucceeded = false;
  const typesWithData = [];

  for (const docType of TYPE_CANDIDATES) {
    try {
      let typeTotal = 0;
      let typeCount = 0;
      let page = null;
      for (let i = 0; i < 5; i += 1) {
        const data = await getJobTreadDocuments(organizationId, {
          size: 100,
          targetUserId,
          ...(page && { page }),
          where: {
            and: [
              ['type', '=', docType],
              ['status', '=', 'pending'],
              ['price', '>', 0]
            ]
          },
          sortBy: [{ field: 'price', order: 'desc' }]
        });
        const documentsNode = data?.organization?.documents;
        const nodes = documentsNode?.nodes ?? [];
        const list = (Array.isArray(nodes) ? nodes : [nodes]).filter(Boolean);
        for (const d of list) {
          typeTotal += parseCurrencyValue(d.price) ?? 0;
          typeCount += 1;
        }
        diagnostics.pages += 1;
        page = documentsNode?.nextPage || null;
        if (!page || list.length === 0) break;
      }
      anySucceeded = true;
      diagnostics.byType[docType] = { count: typeCount, total: Math.round(typeTotal) };
      if (page) {
        diagnostics.warnings.push(`More than 500 '${docType}' bills — the smallest are not included.`);
      }
      if (typeCount > 0) {
        typesWithData.push(docType);
        total += typeTotal;
        count += typeCount;
      }
    } catch (e) {
      // Invalid document type for this org's schema — record and try the next.
      diagnostics.byType[docType] = { error: e?.message || String(e) };
    }
  }

  diagnostics.typeUsed = typesWithData.join(', ') || null;
  if (!anySucceeded) {
    diagnostics.error = 'No vendor-bill document type was accepted by JobTread.';
    console.error('getJobTreadPayables error:', diagnostics.byType);
  } else if (count === 0) {
    diagnostics.warnings.push(`No pending vendor bills found in JobTread (queried: ${TYPE_CANDIDATES.join(', ')}).`);
  }
  return { total: Math.round(total), count, diagnostics };
};

/**
 * Test the JobTread connection by getting current grant info
 * @param {string} [grantKey] - A key the user just typed, to validate before saving.
 *   Omit to test the caller's stored grant (resolved server-side).
 */
export const testJobTreadConnection = async (grantKey = null) => {
  const query = {
    currentGrant: {
      id: {}
    }
  };

  return await makeJobTreadQuery(query, null, { grantKey });
};

/**
 * Get custom fields for an organization
 */
export const getJobTreadCustomFields = async (organizationId, { targetUserId = null } = {}) => {
  const build = (page, withSort) => ({
    organization: {
      $: { id: organizationId },
      id: {},
      customFields: {
        $: {
          size: 100,
          ...(page && { page }),
          ...(withSort && { sortBy: [{ field: 'targetType' }, { field: 'position' }] })
        },
        nextPage: {},
        nodes: {
          id: {},
          name: {},
          type: {},
          targetType: {}
        }
      }
    }
  });

  // Paginate — without an explicit size Pave returns a small default page,
  // silently truncating orgs with many custom fields (and the targetType sort
  // pushed whole categories past the cutoff). Sort is best-effort.
  const allNodes = [];
  let page = null;
  let withSort = true;
  for (let i = 0; i < 5; i += 1) {
    let data;
    try {
      data = await makeJobTreadQuery(build(page, withSort), targetUserId);
    } catch (e) {
      if (!withSort) throw e;
      console.warn('Custom-fields query rejected the sort — retrying unsorted:', e?.message);
      withSort = false;
      data = await makeJobTreadQuery(build(page, withSort), targetUserId);
    }
    const conn = data?.organization?.customFields;
    const nodes = conn?.nodes ?? [];
    const list = (Array.isArray(nodes) ? nodes : [nodes]).filter(Boolean);
    allNodes.push(...list);
    page = conn?.nextPage || null;
    if (!page || list.length === 0) break;
  }
  return { organization: { customFields: { nodes: allNodes } } };
};

/**
 * Resolve the JobTread org from a getJobTreadOrganizations() response.
 * Centralized here — previously duplicated (and silently first-picking) in
 * useSalesDashboard, useMarketingDashboard, useFinanceDashboard and CloseRatio.
 *
 * @returns {{ orgId: string|null, orgName: string|null, orgCount: number, multipleOrgs: boolean }}
 */
export const resolveJobTreadOrg = (orgsData) => {
  const nodes = orgsData?.currentGrant?.user?.memberships?.nodes ?? [];
  const list = (Array.isArray(nodes) ? nodes : [nodes]).filter((n) => n?.organization);
  const org = list[0]?.organization || null;
  return {
    orgId: org?.id || null,
    orgName: org?.name || null,
    orgCount: list.length,
    multipleOrgs: list.length > 1
  };
};

/**
 * Map JobTread jobs to a unified leads list for close ratio and marketing.
 * Uses jobs as the source (JobTread may not expose a separate leads API).
 * Contract value / lead source / probability come from the org's custom
 * fields via the persisted field mapping (see jobtreadFieldMapping.js).
 *
 * @param {string} organizationId
 * @param {{ mapping?: Object|null, targetUserId?: string|null }} [options] - resolved field mapping
 * @returns {Promise<{ leads: Array, diagnostics: Object }>}
 */
export const getJobTreadLeads = async (organizationId, { mapping = null, targetUserId = null } = {}) => {
  const diagnostics = {
    source: 'jobtread',
    orgId: organizationId,
    enriched: false,
    enrichError: null,
    totalJobs: 0,
    hasMoreJobs: false,
    jobsWithContractValue: 0,
    jobsWithLeadSource: 0,
    jobsWithStatus: 0,
    jobsWithClosedOn: 0,
    mappingUsed: {
      leadSource: mapping?.fields?.leadSource?.fieldName || null,
      contractValue: mapping?.fields?.contractValue?.fieldName || null,
      jobStatus: mapping?.fields?.jobStatus?.fieldName || null,
      probability: mapping?.fields?.probability?.fieldName || null
    },
    unresolved: mapping?.unresolved || [],
    ambiguous: mapping?.ambiguous || {},
    warnings: [],
    error: null
  };
  try {
    // Newest jobs first — without a sort the API's default order can make the
    // fetched window all historical jobs, showing zero leads in recent ranges.
    // Paged small so customFieldValues stay under JobTread's response limit.
    const { items: list, enriched, enrichError, droppedFields, hasMore } = await fetchEnrichedPaged({
      buildQuery: (opts, nodes) => buildJobsQuery(organizationId, opts, nodes),
      getConnection: (data) => data?.organization?.jobs,
      fullNodes: ENRICHED_JOB_NODES,
      basicNodes: BASIC_JOB_NODES,
      maxItems: 100,
      pageSize: 20,
      targetUserId,
      baseOptions: { sortBy: [{ field: 'createdAt', order: 'desc' }] }
    });
    diagnostics.enriched = enriched;
    diagnostics.enrichError = enrichError;
    diagnostics.droppedFields = droppedFields || [];
    if (droppedFields?.length) {
      diagnostics.warnings.push(`Org schema does not support job field${droppedFields.length === 1 ? '' : 's'}: ${droppedFields.join(', ')} — related data is unavailable, custom fields still load.`);
    }
    diagnostics.totalJobs = list.length;
    diagnostics.hasMoreJobs = hasMore;

    const leadSourceFieldId = mapping?.fields?.leadSource?.fieldId || null;
    const contractValueFieldId = mapping?.fields?.contractValue?.fieldId || null;
    const jobStatusFieldId = mapping?.fields?.jobStatus?.fieldId || null;

    const fieldsSeen = new Map();
    const leads = list.map((job) => {
      if (enriched) collectCustomFieldDefs(job, fieldsSeen);
      const cfv = enriched ? extractCustomFieldValues(job) : {};
      const mappedValue = contractValueFieldId ? parseCurrencyValue(cfv[contractValueFieldId]) : null;
      const legacyValue = parseCurrencyValue(job.contractValue || job.designContractValue || job.price);
      const estimatedProjectValue = mappedValue ?? legacyValue; // null (not 0) when unavailable
      const leadSource = (leadSourceFieldId ? cfv[leadSourceFieldId] : null) ?? job.leadSource ?? null;
      const status = String(job.status || (jobStatusFieldId ? cfv[jobStatusFieldId] : '') || '').toLowerCase();
      const closedOn = job.closedOn ?? null;

      if (estimatedProjectValue != null) diagnostics.jobsWithContractValue += 1;
      if (leadSource) diagnostics.jobsWithLeadSource += 1;
      if (status) diagnostics.jobsWithStatus += 1;
      if (closedOn) diagnostics.jobsWithClosedOn += 1;

      return {
        leadId: job.id || job.jobId,
        jobId: job.id,
        createdDate: job.createdAt ?? job.createdDate ?? null,
        status,
        closedOn,
        customerId: job.account?.id ?? job.customerId ?? null,
        customerName: job.account?.name ?? null,
        estimatedProjectValue,
        leadSource,
        // Raw custom-field map so callers can use ANY field as the status /
        // lead-source source (the lead-settings picker chooses which).
        customFields: cfv,
        name: job.name || job.number || '—'
      };
    });

    if (diagnostics.hasMoreJobs) {
      diagnostics.warnings.push(`Only the first 100 JobTread jobs are included — counts and ratios exclude older jobs.`);
    }
    if (!contractValueFieldId) {
      diagnostics.warnings.push('No custom field mapped to contract value — project values from JobTread are unavailable.');
    } else if (diagnostics.jobsWithContractValue === 0 && leads.length > 0) {
      diagnostics.warnings.push(`Mapped contract-value field "${diagnostics.mappingUsed.contractValue}" has no values on the fetched jobs.`);
    }
    if (!leadSourceFieldId) {
      diagnostics.warnings.push('No custom field mapped to lead source — lead-source attribution from JobTread is unavailable.');
    }
    if (!enriched) {
      diagnostics.warnings.push(`Enriched job query failed (${enrichError}) — custom fields, closedOn and customer are unavailable this session.`);
    }
    if (diagnostics.jobsWithStatus === 0 && diagnostics.jobsWithClosedOn === 0 && leads.length > 0) {
      diagnostics.warnings.push('Jobs carry neither a status nor closedOn — close ratio cannot distinguish won jobs.');
    }
    diagnostics.customFieldsSeen = [...fieldsSeen].map(([id, name]) => ({ id, name }));

    return { leads, diagnostics };
  } catch (e) {
    console.error('getJobTreadLeads error:', e);
    diagnostics.error = e?.message || String(e);
    return { leads: [], diagnostics };
  }
};

/**
 * Compute close ratio from JobTread leads: closedCount / totalCount
 * @param {Array<{ status: string }>} leads - from getJobTreadLeads
 * @returns {{ closeRatio: number, closedCount: number, totalCount: number }}
 */
export const computeCloseRatioFromLeads = (leads) => {
  const total = leads?.length ?? 0;
  // A job counts as won when its status matches the closed vocabulary OR
  // JobTread marked it closed via closedOn (covers orgs whose "status" is a
  // custom pipeline field the mapping didn't resolve).
  const closed = (leads || []).filter((l) => !!l.closedOn || isClosedStatus(l.status)).length;
  return {
    closeRatio: total > 0 ? Math.round((closed / total) * 1000) / 10 : 0,
    closedCount: closed,
    totalCount: total
  };
};

/**
 * Aggregate leads by month for charts (month, year, count)
 * @param {Array<{ createdDate: string|null }>} leads
 * @param {number} year - optional year to filter
 * @returns {Array<{ month: string, monthIndex: number, year: number, value: number }>}
 */
export const getLeadsByMonth = (leads, year = null) => {
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const counts = {};
  for (let m = 0; m < 12; m++) counts[m] = 0;
  (leads || []).forEach((l) => {
    const d = l.createdDate ? new Date(l.createdDate) : null;
    if (!d || isNaN(d.getTime())) return;
    const y = d.getFullYear();
    if (year != null && y !== year) return;
    const m = d.getMonth();
    counts[m] = (counts[m] || 0) + 1;
  });
  return monthNames.map((month, i) => ({
    month,
    monthIndex: i + 1,
    year: year ?? new Date().getFullYear(),
    value: counts[i] ?? 0
  }));
};

/**
 * Get proposals/estimates for forecast (proposalValue × probability).
 * Uses documents of type proposal/estimate, or jobs with proposal-like status.
 * @param {string} organizationId
 * @returns {Promise<Array<{ id: string, proposalValue: number, expectedStartDate: string|null, probability: number }>>}
 */
export const getJobTreadProposals = async (organizationId, { mapping = null, targetUserId = null } = {}) => {
  const diagnostics = {
    fromDocuments: 0,
    fromJobs: 0,
    probabilitySource: mapping?.fields?.probability?.fieldName
      ? `custom field "${mapping.fields.probability.fieldName}" (default ${DEFAULT_PROPOSAL_PROBABILITY})`
      : `fixed default ${DEFAULT_PROPOSAL_PROBABILITY} (no probability custom field mapped)`,
    warnings: [],
    error: null
  };
  try {
    const contractValueFieldId = mapping?.fields?.contractValue?.fieldId || null;
    const probabilityFieldId = mapping?.fields?.probability?.fieldId || null;

    const [docsData, jobsRes] = await Promise.all([
      getJobTreadDocuments(organizationId, { size: 100, targetUserId }),
      // Paged small so customFieldValues stay under JobTread's response limit.
      fetchEnrichedPaged({
        buildQuery: (opts, nodes) => buildJobsQuery(organizationId, opts, nodes),
        getConnection: (data) => data?.organization?.jobs,
        fullNodes: ENRICHED_JOB_NODES,
        basicNodes: BASIC_JOB_NODES,
        maxItems: 100,
        pageSize: 20,
        targetUserId
      })
    ]);
    const docNodes = docsData?.organization?.documents?.nodes ?? [];
    const docList = Array.isArray(docNodes) ? docNodes : [docNodes];
    const jobList = jobsRes.items;

    const fromDocs = docList
      .filter((d) => PROPOSAL_DOC_TYPES.some((t) => (d.type || '').toLowerCase().includes(t)))
      .map((d) => ({
        id: d.id,
        proposalValue: parseCurrencyValue(d.price || d.cost) ?? 0,
        expectedStartDate: d.createdAt ?? d.expectedStartDate ?? null,
        probability: DEFAULT_PROPOSAL_PROBABILITY,
        origin: 'document'
      }))
      .filter((p) => p.proposalValue > 0);

    const fromJobs = jobList
      .filter((j) => PROPOSAL_JOB_STATUSES.some((s) => (j.status || '').toLowerCase().includes(s)))
      .map((j) => {
        const cfv = jobsRes.enriched ? extractCustomFieldValues(j) : {};
        const mappedValue = contractValueFieldId ? parseCurrencyValue(cfv[contractValueFieldId]) : null;
        const mappedProbabilityRaw = probabilityFieldId ? parseFloat(cfv[probabilityFieldId]) : NaN;
        // Accept probabilities entered as 0–1 or 0–100.
        const mappedProbability = Number.isFinite(mappedProbabilityRaw)
          ? (mappedProbabilityRaw > 1 ? mappedProbabilityRaw / 100 : mappedProbabilityRaw)
          : null;
        return {
          id: j.id,
          proposalValue: mappedValue ?? parseCurrencyValue(j.contractValue || j.designContractValue) ?? 0,
          expectedStartDate: j.startDate ?? j.createdAt ?? null,
          probability: mappedProbability ?? DEFAULT_PROPOSAL_PROBABILITY,
          origin: 'job'
        };
      })
      .filter((p) => p.proposalValue > 0);

    const seen = new Set();
    const proposals = [...fromDocs, ...fromJobs].filter((p) => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });
    diagnostics.fromDocuments = fromDocs.length;
    diagnostics.fromJobs = fromJobs.length;
    if (!jobsRes.enriched) {
      diagnostics.warnings.push('Enriched job query failed — proposal values from jobs are unavailable this session.');
    }
    return { proposals, diagnostics };
  } catch (e) {
    console.error('getJobTreadProposals error:', e);
    diagnostics.error = e?.message || String(e);
    return { proposals: [], diagnostics };
  }
};

/**
 * Get customers as a leads-style list for close ratio (when user chooses "Customers" as source).
 * JobTread accounts may not have status; if they do, it can be mapped in the UI.
 * @param {string} organizationId
 * @returns {Promise<Array<{ leadId: string, name: string, createdDate: string|null, status: string }>>}
 */
export const getJobTreadCustomersAsLeads = async (organizationId) => {
  try {
    const data = await getJobTreadCustomers(organizationId, { size: 100 });
    const nodes = data?.organization?.accounts?.nodes ?? [];
    const list = Array.isArray(nodes) ? nodes : [nodes];
    return list.map((acc) => ({
      leadId: acc.id,
      name: acc.name || '—',
      createdDate: acc.createdAt ?? null,
      status: (acc.status || '').toLowerCase() || 'open'
    }));
  } catch (e) {
    console.error('getJobTreadCustomersAsLeads error:', e);
    return [];
  }
};

/**
 * Compute a JobTread backlog summary for the Project Runway widget.
 * Backlog = contract value of signed/active jobs (via the contract-value
 * custom field mapping); weighted pipeline = Σ proposalValue × probability.
 * When active-job values can't be resolved (no mapping / no values), backlog
 * falls back to the raw proposal sum — recorded in diagnostics — matching the
 * previous behavior.
 * Returns null on failure so callers can fall back gracefully.
 *
 * @param {string} organizationId
 * @param {{ mapping?: Object|null, targetUserId?: string|null }} [options]
 */
export const getJobTreadBacklog = async (organizationId, { mapping = null, targetUserId = null } = {}) => {
  try {
    const [proposalsRes, jobsRes] = await Promise.all([
      getJobTreadProposals(organizationId, { mapping, targetUserId }),
      // Paged small so customFieldValues stay under JobTread's response limit.
      fetchEnrichedPaged({
        buildQuery: (opts, nodes) => buildJobsQuery(organizationId, opts, nodes),
        getConnection: (data) => data?.organization?.jobs,
        fullNodes: ENRICHED_JOB_NODES,
        basicNodes: BASIC_JOB_NODES,
        maxItems: 100,
        pageSize: 20,
        targetUserId
      })
    ]);
    const proposals = proposalsRes.proposals;

    const jobList = jobsRes.items;
    const contractValueFieldId = mapping?.fields?.contractValue?.fieldId || null;

    const activeJobs = jobList.filter((j) => isActiveStatus(j.status));
    const activeJobsValue = activeJobs.reduce((sum, j) => {
      const cfv = jobsRes.enriched ? extractCustomFieldValues(j) : {};
      const v = contractValueFieldId ? parseCurrencyValue(cfv[contractValueFieldId]) : null;
      return sum + (v ?? 0);
    }, 0);

    const proposalSum = (proposals || []).reduce((sum, p) => sum + (p.proposalValue || 0), 0);
    const weightedPipelineValue = (proposals || []).reduce(
      (sum, p) => sum + (p.proposalValue || 0) * (p.probability ?? DEFAULT_PROPOSAL_PROBABILITY),
      0
    );

    const backlogFromActiveJobs = activeJobsValue > 0;
    const backlogValue = backlogFromActiveJobs ? activeJobsValue : proposalSum;

    return {
      activeJobCount: activeJobs.length,
      totalJobCount: jobList.length,
      proposalCount: (proposals || []).length,
      backlogValue: Math.round(backlogValue),
      weightedPipelineValue: Math.round(weightedPipelineValue),
      diagnostics: {
        backlogBasis: backlogFromActiveJobs
          ? `sum of contract values on ${activeJobs.length} active jobs`
          : 'sum of open proposal values (active-job contract values unavailable)',
        activeJobsValue: Math.round(activeJobsValue),
        proposalSum: Math.round(proposalSum),
        enriched: jobsRes.enriched,
        proposals: proposalsRes.diagnostics,
        warnings: [
          ...(contractValueFieldId ? [] : ['No contract-value custom field mapped — signed backlog falls back to proposal totals.']),
          ...(proposalsRes.diagnostics.warnings || [])
        ]
      }
    };
  } catch (e) {
    console.error('getJobTreadBacklog error:', e);
    return null;
  }
};

export default {
  isJobTreadConnected,
  makeJobTreadQuery,
  getJobTreadOrganizations,
  getJobTreadOrganization,
  getJobTreadAccounts,
  getJobTreadCustomers,
  getJobTreadVendors,
  createJobTreadAccount,
  getJobTreadJobs,
  getJobTreadJobsEnriched,
  extractCustomFieldValues,
  getJobTreadDocuments,
  getJobTreadOpenInvoices,
  testJobTreadConnection,
  getJobTreadCustomFields,
  resolveJobTreadOrg,
  getJobTreadLeads,
  getJobTreadCustomersAsLeads,
  getJobTreadCustomersEnriched,
  getJobTreadCustomerLeads,
  computeCloseRatioFromLeads,
  getLeadsByMonth,
  getJobTreadProposals,
  getJobTreadBacklog
};
